/**
 * miniProjectLifecycle — single source of truth for mapping a
 * Mini-Project's behavioral lifecycle state (draft/active/complete/
 * expired) onto the canonical project_status vocabulary used for
 * sorting, grouping, search, and the isProjectActive/isProjectCompleted
 * predicates.
 *
 * DEV-1271 follow-up (Skip 2026-06-08): the lifecycle status — not the
 * old soft `projectStatus` label — is now the canonical status for
 * Mini-Projects. The App.tsx aggregator synthesizes each Mini-Project's
 * FP-shaped `project_status` through `miniLifecycleToProjectStatus()`,
 * so every downstream consumer (KPI grid grouping, portfolio sort,
 * global/local search, the canonical completion/active predicates)
 * keys off the lifecycle automatically. The soft `projectStatus` field
 * still exists (it's the Settings-modal dropdown) but no longer drives
 * any sort/group/search logic.
 *
 * IMPORTANT — two value kinds, don't conflate:
 *   • CANONICAL value (this map): a stable English string used for
 *     grouping/sort/search/predicates. Never localize it — localized
 *     grouping keys would fragment a group across languages.
 *   • DISPLAY label: localize at the render site via the existing
 *     `pages:projectDetail.tray.lifecycleStates.*` keys.
 *
 * The canonical strings deliberately reuse the project_status enum
 * (Planned / In Progress / Completed) so they group alongside Full
 * Projects and satisfy the predicates (isProjectCompleted → 'COMPLETED';
 * isProjectActive terminal set → 'ABANDONED' | 'COMPLETED'). 'Expired'
 * is an additional non-terminal value (an expired Mini-Project can be
 * Reopened, so it is NOT treated as terminal/closed).
 */

/** Lifecycle state → canonical project_status string. */
export const MINI_LIFECYCLE_TO_PROJECT_STATUS: Record<string, string> = {
  draft: 'Planned',
  active: 'In Progress',
  complete: 'Completed',
  expired: 'Expired',
};

/**
 * Map a Mini-Project lifecycle status to its canonical project_status
 * string for sort/group/search/predicates. Returns '' for an unknown
 * state so callers can fall back to the raw value if they wish.
 */
export function miniLifecycleToProjectStatus(
  lifecycleStatus: string | null | undefined,
): string {
  return MINI_LIFECYCLE_TO_PROJECT_STATUS[String(lifecycleStatus || '').toLowerCase()] ?? '';
}
